import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    X,
    ChevronLeft,
    ChevronRight,
    Search,
    Heart,
    Music,
    Download,
    Wifi,
    Sparkles,
    UserPlus
} from 'lucide-react';

const ONBOARDING_KEY = 'jem-onboarding-completed';

interface OnboardingSlide {
    icon: React.ReactNode;
    title: string;
    description: string;
    color: string;
    hasAccountLink?: boolean;
}

const slides: OnboardingSlide[] = [
    {
        icon: <Sparkles className="w-12 h-12" />,
        title: 'Bienvenue dans JEM Chants !',
        description: 'Découvrez les 1199 chants de Jeunesse en Mission, avec paroles et partitions.',
        color: 'from-blue-500 to-indigo-600'
    },
    {
        icon: <Search className="w-12 h-12" />,
        title: 'Recherche Puissante',
        description: 'Trouvez rapidement un chant par numéro, titre, thème ou tonalité. Filtrez par catégorie JEM.',
        color: 'from-green-500 to-emerald-600'
    },
    {
        icon: <Heart className="w-12 h-12" />,
        title: 'Vos Favoris',
        description: 'Ajoutez des chants à vos favoris pour y accéder rapidement. Votre historique est automatiquement sauvegardé.',
        color: 'from-red-500 to-pink-600'
    },
    {
        icon: <Music className="w-12 h-12" />,
        title: 'Lecteur MIDI',
        description: 'Écoutez les chants avec notre lecteur MIDI intégré. Créez votre playlist personnalisée.',
        color: 'from-purple-500 to-violet-600'
    },
    {
        icon: <Wifi className="w-12 h-12" />,
        title: 'Mode Hors Ligne',
        description: 'Téléchargez les partitions et chants pour les consulter sans connexion internet.',
        color: 'from-orange-500 to-amber-600'
    },
    {
        icon: <UserPlus className="w-12 h-12" />,
        title: 'Créez votre compte',
        description: 'Synchronisez vos favoris, playlists et historique sur tous vos appareils. Gratuitement !',
        color: 'from-cyan-500 to-teal-600',
        hasAccountLink: true
    }
];

interface OnboardingModalProps {
    onComplete: () => void;
}

export function OnboardingModal({ onComplete }: OnboardingModalProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Animation d'entrée
        setTimeout(() => setIsVisible(true), 100);
    }, []);

    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            handleComplete();
        }
    };

    const handlePrev = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleComplete = () => {
        localStorage.setItem(ONBOARDING_KEY, 'true');
        setIsVisible(false);
        setTimeout(onComplete, 300);
    };

    const handleSkip = () => {
        handleComplete();
    };

    const slide = slides[currentSlide];

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
                }`}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleSkip}
            />

            {/* Modal */}
            <div
                className={`relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transition-transform duration-300 ${isVisible ? 'scale-100' : 'scale-95'
                    }`}
            >
                {/* Skip button */}
                <button
                    onClick={handleSkip}
                    className="absolute top-4 right-4 z-10 p-2 text-white/80 hover:text-white transition-colors"
                    aria-label="Fermer"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Gradient header with icon */}
                <div className={`bg-gradient-to-br ${slide.color} p-8 text-center text-white transition-all duration-300`}>
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
                        {slide.icon}
                    </div>
                    <h2 className="text-xl font-bold">{slide.title}</h2>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-center text-gray-600 dark:text-gray-300 mb-4 min-h-[60px]">
                        {slide.description}
                    </p>

                    {/* Account creation link */}
                    {slide.hasAccountLink && (
                        <div className="flex justify-center gap-3 mb-4">
                            <Link
                                to="/register"
                                onClick={handleComplete}
                                className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors text-sm"
                            >
                                Créer un compte
                            </Link>
                            <Link
                                to="/login"
                                onClick={handleComplete}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                            >
                                Se connecter
                            </Link>
                        </div>
                    )}

                    {/* Dots indicator */}
                    <div className="flex justify-center gap-2 mb-6">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentSlide
                                    ? 'bg-blue-600 w-6'
                                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                                    }`}
                                aria-label={`Slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex gap-3">
                        {currentSlide > 0 && (
                            <button
                                onClick={handlePrev}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                                Précédent
                            </button>
                        )}
                        <button
                            onClick={handleNext}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r ${slide.color} text-white rounded-lg font-medium hover:opacity-90 transition-opacity`}
                        >
                            {currentSlide === slides.length - 1 ? (
                                <>
                                    <Download className="w-5 h-5" />
                                    Commencer
                                </>
                            ) : (
                                <>
                                    Suivant
                                    <ChevronRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Hook to check if onboarding should be shown
 */
export function useOnboarding() {
    const [showOnboarding, setShowOnboarding] = useState(false);

    useEffect(() => {
        const completed = localStorage.getItem(ONBOARDING_KEY);
        if (!completed) {
            // Small delay to let the app load first
            setTimeout(() => setShowOnboarding(true), 500);
        }
    }, []);

    const completeOnboarding = () => {
        setShowOnboarding(false);
    };

    return { showOnboarding, completeOnboarding };
}
